// src/components/ContactPage.tsx

import React, { useState, useEffect } from 'react'; 
import api from '../services/api'; 
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.tsx';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { Textarea } from './ui/textarea.tsx';
import { Mail, Send, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export function ContactPage() {
  const [loading, setLoading] = useState(false);
  
  // 1. ADIM: ProfileSettingsPage'deki gibi userId'yi alıyoruz
  const userId = localStorage.getItem("userId");

  // Form State'i
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '', 
    subject: '',
    message: ''
  });

  // --- KULLANICI BİLGİLERİNİ ÇEKME (ProfileSettingsPage ile AYNI YÖNTEM) ---
  useEffect(() => {
    if (!userId) return;

    const fetchUserInfo = async () => {
      try {
        const response = await api.get(`/users/${userId}/`);
        const data = response.data;
        
        const fName = data.FirstName || '';
        const lName = data.LastName || '';
        let fullName = `${fName} ${lName}`.trim();

        if (!fullName) fullName = data.username || '';

        setFormData(prev => ({
          ...prev,
          name: fullName,
          email: data.email || '',       
          phone: data.PhoneNumber || ''  
        }));

      } catch (error) {
        console.error("Kullanıcı bilgileri otomatik doldurulamadı:", error);
      }
    };

    fetchUserInfo();
  }, [userId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: `${formData.message}\n\nİletişim Tel: ${formData.phone}`
      };

      await api.post('/contact/', payload);
      
      toast.success('Mesajınız başarıyla gönderildi! En kısa sürede dönüş yapacağız.');
      
      setFormData(prev => ({ ...prev, subject: '', message: '' }));
      
    } catch (error) {
      console.error(error);
      toast.error('Mesaj gönderilemedi. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Sol Taraf: İletişim Formu */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.5 }}
          className="lg:col-span-2"
        >
          <Card className="h-full border-slate-200/80 shadow-md">
            <CardHeader>
              <CardTitle>Bize Ulaşın</CardTitle>
              <CardDescription>
                Formu doldurarak bizimle iletişime geçebilirsiniz.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Ad Soyad</Label>
                    <Input 
                        id="name" 
                        placeholder="Adınız Soyadınız" 
                        required 
                        value={formData.name} 
                        onChange={handleChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-posta</Label>
                    <Input 
                        id="email" 
                        type="email" 
                        placeholder="ornek@email.com" 
                        required 
                        value={formData.email} 
                        onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon</Label>
                    <Input 
                        id="phone" 
                        type="tel" 
                        placeholder="+90 5XX XXX XX XX" 
                        value={formData.phone} 
                        onChange={handleChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Konu</Label>
                    <Input 
                        id="subject" 
                        placeholder="Mesaj konusu" 
                        required 
                        value={formData.subject}
                        onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Mesajınız</Label>
                  <Textarea 
                    id="message" 
                    placeholder="Mesajınızı buraya yazın..." 
                    rows={6}
                    required 
                    value={formData.message}
                    onChange={handleChange}
                  />
                </div>

                <Button type="submit" size="lg" className="w-full" disabled={loading}>
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Sağ Taraf: Sosyal Medya ve E-posta */}
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="border-slate-200/80 shadow-md">
              <CardHeader>
                <CardTitle>Sosyal Medya ve E-posta</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <a 
                  href="https://www.instagram.com/sdal_mezunlari/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-full block"
                >
                  <Button variant="outline" className="w-full justify-start hover:bg-pink-50 hover:text-pink-600 hover:border-pink-200 transition-colors">
                    <Instagram className="mr-2 h-4 w-4" />
                    Instagram'da Takip Et
                  </Button>
                </a>

                <a 
                  href="mailto:iletisim@sdalmezunlari.org.tr" 
                  className="w-full block"
                >
                  <Button variant="outline" className="w-full justify-start hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors">
                    <Mail className="mr-2 h-4 w-4" />
                    iletisim@sdalmezunlari.org.tr
                  </Button>
                </a>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}